import {
  BadgeNext as Badge,
  Flex,
  TabNext as Tab,
  Text,
} from "@momentum-ui/react-collaboration";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { VM_STORAGE_KEYS } from "../../packages/adapter-interfaces/src";
import { Logger } from "../../packages/logger/src/Logger";
import { submitMetrics } from "../../packages/widgets/src/services/WebexMetricsService";
import { activeTabBarMetric } from "../../packages/widgets/src/utils/MetricUtils";
import {
  THEME_CLASS,
  WEBEX_CALLING_THEME,
} from "../../packages/widgets/src/utils/ThemeUtils";
import { getMSTeamsContext } from "../../ms-teams-sdk/util/MsTeamsContextUtil";
import { setLocalStorageItem } from "../../ms-teams-sdk/util/LocalStorageUtil";
import { useMsTeamsTheme } from "../../hooks/useMsTeamsTheme";
import "./TabBar.scss";

/** Stable ids for selection and React keys — do not use translated `label` (breaks on locale change and remounts presence). */
export const WEBEX_TAB_ID = {
  DIALPAD: "dialpad",
  SPEED_DIAL: "speedDial",
  RECENT_CALLS: "recentCalls",
  MISSED: "missed",
  VOICEMAIL: "voicemail",
} as const;

export type WebexTabId = typeof WEBEX_TAB_ID[keyof typeof WEBEX_TAB_ID];

export interface ITabBarProps {
  children: React.ReactElement<ITabBarItemProps | undefined>[];
  /** When omitted, desktop default is Recent calls + Dialpad (two-column shell). */
  defaultActiveTabs?: WebexTabId[];
  appearance?: "default" | "mobilePill";
}

export interface ITabBarItemProps {
  tabId: WebexTabId;
  label: string;
  badgeCount?: number;
  children: JSX.Element | null;
  onTabPress?: () => void;
  onActiveRightItem?: React.ReactElement;
}

/**
 *
 * @param props
 */
export function TabBarItem(props: ITabBarItemProps): JSX.Element | null {
  return props.children;
}

const isTabChild = (
  child: React.ReactElement | null
): child is React.ReactElement => child !== null;

/**
 *
 * @param root0
 * @param root0.items
 * @param root0.children
 * @param root0.rest
 */

export const TabBar = ({
  children,
  defaultActiveTabs = undefined,
  appearance = "default",
}: ITabBarProps): JSX.Element => {
  const filteredChildren = children.filter((child) => child);
  const { t } = useTranslation();
  const [theme] = useMsTeamsTheme();
  let themeClass = " ";
  if (theme === WEBEX_CALLING_THEME.DARK) {
    themeClass = THEME_CLASS.DARK;
  } else if (theme === WEBEX_CALLING_THEME.LIGHT) {
    themeClass = THEME_CLASS.LIGHT;
  }
  const [focusedTabId, setFocusedTabId] = useState<string>("");
  const [focusedLabel, setFocusedLabel] = useState<string>("");
  const fallbackActiveTabs = defaultActiveTabs ?? [
    WEBEX_TAB_ID.RECENT_CALLS,
    WEBEX_TAB_ID.DIALPAD,
  ];
  const isSinglePane = (defaultActiveTabs?.length ?? 0) === 1;
  const [activeTabs, setActiveTabs] = useState<string[]>(fallbackActiveTabs);
  const resolveActiveTabs = (tabs: string[]) =>
    isSinglePane ? [tabs[0]] : tabs;
  const availableTabIdsKey = filteredChildren
    .map((child) => child.props?.tabId)
    .filter(Boolean)
    .join("|");
  const fallbackActiveTabsKey = fallbackActiveTabs.join("|");
  const availableTabIds = filteredChildren
    .map((child) => child.props?.tabId)
    .filter(Boolean);
  const keepsDialpadAsDesktopSecondaryPane =
    availableTabIds.includes(WEBEX_TAB_ID.DIALPAD) &&
    availableTabIds.some(
      (tabId) =>
        tabId !== WEBEX_TAB_ID.DIALPAD && tabId !== WEBEX_TAB_ID.SPEED_DIAL
    );

  const defaultRecentCalls = t("recentCalls");
  const tabMappings: Record<string, string[]> = {
    [t("voicemail")]: [WEBEX_TAB_ID.VOICEMAIL, WEBEX_TAB_ID.DIALPAD],
    [defaultRecentCalls]: [WEBEX_TAB_ID.RECENT_CALLS, WEBEX_TAB_ID.DIALPAD],
    [t("recents")]: [WEBEX_TAB_ID.RECENT_CALLS, WEBEX_TAB_ID.DIALPAD],
    [t("missed")]: [WEBEX_TAB_ID.MISSED, WEBEX_TAB_ID.DIALPAD],
  };

  if (filteredChildren.length === 0) {
    throw new Error("No tabs available for TabBar");
  }

  const fetchClientType = async () => {
    const context = await getMSTeamsContext();
    if (context?.page?.subPageId) {
      if (tabMappings[context.page.subPageId]) {
        const selectedTabs = tabMappings[context.page.subPageId];
        Logger.info(
          `Tab is selected through deepLinking: ${context.page.subPageId}`
        );
        const nextActiveTabs = resolveActiveTabs(selectedTabs);
        flushSync(() => {
          setActiveTabs(nextActiveTabs);
        });
        const selectedTabId = nextActiveTabs[0];
        filteredChildren
          .find((child) => child.props?.tabId === selectedTabId)
          ?.props?.onTabPress?.();
      } else {
        Logger.info(
          `Deep linking failed. Default tabs selected: ${context.page.subPageId}`
        );
        setActiveTabs(resolveActiveTabs(fallbackActiveTabs));
      }
    } else {
      Logger.info(`Default tabs selected on page load without deep linking`);
      setActiveTabs(resolveActiveTabs(fallbackActiveTabs));
    }
  };

  const getNextActiveTabs = (tabId: string): string[] => {
    if (isSinglePane) {
      return [tabId];
    }

    // The desktop shell renders two independent TabBars. The dialpad/speed
    // dial bar must remain a single-select group, while a bar that contains a
    // call-history tab alongside Dialpad keeps Dialpad visible as its second
    // pane. Infer the layout from the tabs in this instance instead of
    // applying the two-pane rule to every desktop tab click.
    if (!keepsDialpadAsDesktopSecondaryPane) {
      return [tabId];
    }

    if (tabId === WEBEX_TAB_ID.DIALPAD) {
      const pairedTabId =
        activeTabs.find(
          (activeTabId) => activeTabId !== WEBEX_TAB_ID.DIALPAD
        ) ??
        fallbackActiveTabs.find(
          (activeTabId) => activeTabId !== WEBEX_TAB_ID.DIALPAD
        ) ??
        WEBEX_TAB_ID.DIALPAD;
      return Array.from(new Set([pairedTabId, WEBEX_TAB_ID.DIALPAD]));
    }

    return [tabId, WEBEX_TAB_ID.DIALPAD];
  };

  const onTabPressCallback = (
    tabId: string,
    label: string,
    onTabPress?: () => void
  ) => {
    const nextActiveTabs = getNextActiveTabs(tabId);
    flushSync(() => {
      setFocusedTabId(tabId);
      setFocusedLabel(label);
      setActiveTabs(nextActiveTabs);
    });
    if (onTabPress && !activeTabs.includes(tabId)) {
      onTabPress();
    }
    submitMetrics({
      metricEvent: activeTabBarMetric,
      activeTab: tabId,
    });
  };

  const sectionRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  const [isScrollbarVisible, setIsScrollbarVisible] = useState<boolean>(false);
  const onTabFocus = (tabId: string, label: string): void => {
    setFocusedTabId(tabId);
    setFocusedLabel(label);
  };

  useEffect(() => {
    // Calculate the badge count for recent calls
    const recentCallsBadgeCount = filteredChildren.reduce(
      (accumulator, child) => {
        return child?.props?.tabId === WEBEX_TAB_ID.RECENT_CALLS
          ? accumulator + (child?.props?.badgeCount || 0)
          : accumulator;
      },
      0
    );
    const missedBadgeCount = filteredChildren.reduce(
      (accumulator, child) => {
        return child?.props?.tabId === WEBEX_TAB_ID.MISSED
          ? accumulator + (child?.props?.badgeCount || 0)
          : accumulator;
      },
      0
    );
    // Calculate the badge count for voicemails
    const voicemailBadgeCount = filteredChildren.reduce(
      (accumulator, child) => {
        return child?.props?.tabId === WEBEX_TAB_ID.VOICEMAIL
          ? accumulator + (child?.props?.badgeCount || 0)
          : accumulator;
      },
      0
    );
    if (
      focusedTabId === WEBEX_TAB_ID.DIALPAD &&
      !activeTabs.includes(WEBEX_TAB_ID.DIALPAD)
    ) {
      setFocusedLabel(t("label.dialpad.tab"));
    } else if (
      focusedTabId === WEBEX_TAB_ID.DIALPAD &&
      activeTabs.includes(WEBEX_TAB_ID.DIALPAD)
    ) {
      setFocusedLabel(t("label.dialpad.selected.tab"));
    } else if (
      focusedTabId === WEBEX_TAB_ID.SPEED_DIAL &&
      !activeTabs.includes(WEBEX_TAB_ID.SPEED_DIAL)
    ) {
      setFocusedLabel(t("label.speedDial.tab"));
    } else if (
      focusedTabId === WEBEX_TAB_ID.SPEED_DIAL &&
      activeTabs.includes(WEBEX_TAB_ID.SPEED_DIAL)
    ) {
      setFocusedLabel(t("label.speedDial.selected.tab"));
    } else if (
      focusedTabId === WEBEX_TAB_ID.RECENT_CALLS &&
      !activeTabs.includes(WEBEX_TAB_ID.RECENT_CALLS)
    ) {
      if (recentCallsBadgeCount > 0) {
        if (recentCallsBadgeCount === 1) {
          setFocusedLabel(
            `${t("label.recentCalls.tab.withBadge", {
              count: recentCallsBadgeCount,
            })}`
          );
        } else {
          setFocusedLabel(
            `${t("label.recentCalls.tab.withMultipleBadges", {
              count: recentCallsBadgeCount,
            })}`
          );
        }
      } else {
        setFocusedLabel(t("label.recentCalls.tab"));
      }
    } else if (
      focusedTabId === WEBEX_TAB_ID.RECENT_CALLS &&
      activeTabs.includes(WEBEX_TAB_ID.RECENT_CALLS)
    ) {
      if (recentCallsBadgeCount > 0) {
        if (recentCallsBadgeCount === 1) {
          setFocusedLabel(
            `${t("label.recentCalls.selected.tab.withBadge", {
              count: recentCallsBadgeCount,
            })}`
          );
        } else {
          setFocusedLabel(
            `${t("label.recentCalls.selected.tab.withMultipleBadges", {
              count: recentCallsBadgeCount,
            })}`
          );
        }
      } else {
        setFocusedLabel(t("label.recentCalls.selected.tab"));
      }
    } else if (
      focusedTabId === WEBEX_TAB_ID.MISSED &&
      !activeTabs.includes(WEBEX_TAB_ID.MISSED)
    ) {
      if (missedBadgeCount > 0) {
        if (missedBadgeCount === 1) {
          setFocusedLabel(
            `${t("label.missed.tab.withBadge", {
              count: missedBadgeCount,
            })}`
          );
        } else {
          setFocusedLabel(
            `${t("label.missed.tab.withMultipleBadges", {
              count: missedBadgeCount,
            })}`
          );
        }
      } else {
        setFocusedLabel(t("label.missed.tab"));
      }
    } else if (
      focusedTabId === WEBEX_TAB_ID.MISSED &&
      activeTabs.includes(WEBEX_TAB_ID.MISSED)
    ) {
      if (missedBadgeCount > 0) {
        if (missedBadgeCount === 1) {
          setFocusedLabel(
            `${t("label.missed.selected.tab.withBadge", {
              count: missedBadgeCount,
            })}`
          );
        } else {
          setFocusedLabel(
            `${t("label.missed.selected.tab.withMultipleBadges", {
              count: missedBadgeCount,
            })}`
          );
        }
      } else {
        setFocusedLabel(t("label.missed.selected.tab"));
      }
    } else if (
      focusedTabId === WEBEX_TAB_ID.VOICEMAIL &&
      !activeTabs.includes(WEBEX_TAB_ID.VOICEMAIL)
    ) {
      if (voicemailBadgeCount > 0) {
        if (voicemailBadgeCount === 1) {
          setFocusedLabel(
            t("label.voicemail.tab.withBadge", { count: voicemailBadgeCount })
          );
        } else {
          setFocusedLabel(
            `${t("label.voicemail.tab.withMultipleBadges", {
              count: voicemailBadgeCount,
            })}`
          );
        }
      } else {
        setFocusedLabel(t("label.voicemail.tab"));
      }
    } else if (
      focusedTabId === WEBEX_TAB_ID.VOICEMAIL &&
      activeTabs.includes(WEBEX_TAB_ID.VOICEMAIL)
    ) {
      if (voicemailBadgeCount > 0) {
        if (voicemailBadgeCount === 1) {
          setFocusedLabel(
            t("label.voicemail.selected.tab.withBadge", {
              count: voicemailBadgeCount,
            })
          );
        } else {
          setFocusedLabel(
            `${t("label.voicemail.selected.tab.withMultipleBadges", {
              count: voicemailBadgeCount,
            })}`
          );
        }
      } else {
        setFocusedLabel(t("label.voicemail.selected.tab"));
      }
    }
  }, [focusedLabel, focusedTabId, activeTabs, filteredChildren, t]);

  useEffect(() => {
    fetchClientType();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- do not re-run on language change; avoids resetting tab selection and remounting presence UIs
  }, []);

  useEffect(() => {
    setLocalStorageItem(
      VM_STORAGE_KEYS.VM_TAB,
      activeTabs.includes(WEBEX_TAB_ID.VOICEMAIL)
    );
  }, [activeTabs]);

  // Momentum owns the mobile tab colors. Keep the legacy scrollbar compensation
  // on the desktop appearance only; forcing `scroll` on the mobile panel adds a
  // scrollbar even when the responsive dialpad content fits its viewport.
  const shouldReserveDialpadScrollbar =
    appearance !== "mobilePill" &&
    isScrollbarVisible &&
    activeTabs.includes(WEBEX_TAB_ID.DIALPAD);

  useEffect(() => {
    const availableTabIdsForEffect = availableTabIdsKey
      .split("|")
      .filter(Boolean);
    if (activeTabs.every((tabId) => availableTabIdsForEffect.includes(tabId))) {
      return;
    }

    const availableFallbackTabs = fallbackActiveTabsKey
      .split("|")
      .filter((tabId) => availableTabIdsForEffect.includes(tabId));
    let nextActiveTabs = availableFallbackTabs;
    if (availableFallbackTabs.length === 0) {
      nextActiveTabs = availableTabIdsForEffect.slice(0, 1);
    } else if (isSinglePane) {
      nextActiveTabs = availableFallbackTabs.slice(0, 1);
    }

    setActiveTabs(nextActiveTabs);
  }, [activeTabs, availableTabIdsKey, fallbackActiveTabsKey, isSinglePane]);

  // Momentum Tab/ButtonSimple keeps aria-label but drops tab-set metadata.
  useLayoutEffect(() => {
    if (appearance !== "mobilePill") {
      return;
    }

    const tabs = tabListRef.current?.querySelectorAll<HTMLElement>(
      ".tab-bar__tab"
    );
    if (!tabs) {
      return;
    }

    tabs.forEach((tab, index) => {
      tab.setAttribute("aria-posinset", `${index + 1}`);
      tab.setAttribute("aria-setsize", `${tabs.length}`);
    });
  }, [appearance, availableTabIdsKey]);

  useEffect(() => {
    const checkScrollbar = () => {
      if (sectionRef.current?.previousElementSibling) {
        const sectionContentHeight =
          sectionRef.current?.previousElementSibling.scrollHeight;
        const sectionContainerHeight =
          sectionRef.current?.previousElementSibling.clientHeight;
        const scrollbarVisible = sectionContentHeight > sectionContainerHeight;
        setIsScrollbarVisible(scrollbarVisible);
      }
    };

    // Call the checkScrollbar function initially to determine the initial scrollbar state
    checkScrollbar();

    // Use ResizeObserver to watch for changes in the section's size
    const resizeObserver = new ResizeObserver(checkScrollbar);
    if (sectionRef.current?.previousElementSibling) {
      resizeObserver.observe(sectionRef.current?.previousElementSibling);
    }

    // Detach the ResizeObserver on unmount
    return () => {
      if (sectionRef.current?.previousElementSibling) {
        resizeObserver.unobserve(sectionRef.current?.previousElementSibling);
      }
    };
  }, []);

  return (
    <Flex className="tab-bar-container" direction="column" alignItems="stretch">
      <Flex
        alignItems="center"
        xgap={appearance === "mobilePill" ? undefined : "1rem"}
        className={[
          "tab-bar",
          appearance === "mobilePill" && "tab-bar--mobile-pill",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div ref={tabListRef} style={{ display: "contents" }}>
        {filteredChildren.map((child, index) => {
          const { tabId, label, badgeCount, onTabPress } = child.props!;
          const isActive = activeTabs.includes(tabId);
          const shouldUseMobileTabSetMetadata = appearance === "mobilePill";
          const badge = badgeCount ? (
            <Badge key="badge" size={18}>{`${badgeCount}`}</Badge>
          ) : null;
          const tabChildren = [
            badge,
            <Text
              key="label"
              type="subheader-secondary"
              className={
                appearance === "mobilePill"
                  ? "tab-bar__label--mobile-pill"
                  : `${isActive ? `enabled-color` : `disabled-color`}`
              }
            >
              {label}
            </Text>,
          ].filter(isTabChild);

          return (
            <Tab
              key={tabId}
              active={isActive}
              className={[
                "tab-bar__tab",
                appearance === "mobilePill" && "tab-bar__tab--mobile-pill",
                isActive && "tab-bar__tab--active",
              ]
                .filter(Boolean)
                .join(" ")}
              onPress={() => onTabPressCallback(tabId, label, onTabPress)}
              onFocus={() => onTabFocus(tabId, label)}
              aria-label={
                shouldUseMobileTabSetMetadata
                  ? undefined
                  : focusedTabId === tabId
                    ? focusedLabel
                    : undefined
              }
              aria-posinset={
                shouldUseMobileTabSetMetadata ? index + 1 : undefined
              }
              aria-setsize={
                shouldUseMobileTabSetMetadata ? filteredChildren.length : undefined
              }
            >
              {tabChildren}
            </Tab>
          );
        })}
        </div>
        <div className="tab-bar__spacer" style={{ flexGrow: 1 }} />
        {filteredChildren
          .filter((child) => {
            return (
              activeTabs.includes(child.props?.tabId as string) &&
              child.props?.onActiveRightItem
            );
          })
          .map((child) => (
            <React.Fragment key={`${child.props?.tabId}-action`}>
              {child.props?.onActiveRightItem}
            </React.Fragment>
          ))}
      </Flex>
      {filteredChildren.map((child) => {
        const tid = child.props!.tabId as string;
        const isActiveTab = activeTabs.includes(tid);
        return (
          <div
            ref={sectionRef}
            style={{
              display: isActiveTab ? "block" : "none",
              overflowY: shouldReserveDialpadScrollbar ? "scroll" : "auto",
              paddingLeft: shouldReserveDialpadScrollbar ? "15.5px" : "0",
              marginRight: "0.25rem",
            }}
            key={tid}
            className={`tab-bar-content ${themeClass}`}
          >
            {child.props!.children
              ? React.cloneElement(child.props!.children, { isActiveTab })
              : null}
          </div>
        );
      })}
    </Flex>
  );
};
